import React from 'react';
import { Input, Button, Form, Card, message, Upload, List, Select, Tooltip, Tag, Modal } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import ConsultantService, {
    ConsultantPostParam,
    ConsultantPutParam,
    IConsultantAttachment,
    IConsultantExpertiseTag,
    IConsultantInfo,
} from '../../service/consultant/ConsultantService';
import CooperationStatesSelect, { CooperationStates } from '../../components/public/CooperationStatesSelect';
import {
    ADD_CONSULTANT_ERROR_MESSAGE,
    ADD_CONSULTANT_SUCCESS_MESSAGE,
    EDIT_CONSULTANT_ERROR_MESSAGE,
    EDIT_CONSULTANT_SUCCESS_MESSAGE,
    UPLOAD_CONSULTANT_ATTACHMENT_ERROR_MESSAGE,
    UPLOAD_CONSULTANT_ATTACHMENT_SUCCESS_MESSAGE,
} from '../../config/strcfg';
import './page-consultant.less';
import { buildFileUrl } from '../../utils/fileTools';

interface ConsultantManagerPageState {
    loading: boolean;
    aiTagLoading: boolean;
    isEditMode: boolean;
    data: IConsultantInfo | null;
    attachmentList: IConsultantAttachment[];
}

class ConsultantManagerPage extends React.Component<any, ConsultantManagerPageState> {
    state: ConsultantManagerPageState = {
        loading: false,
        aiTagLoading: false,
        isEditMode: false,
        data: null,
        attachmentList: [],
    };

    formRef = React.createRef<FormInstance>();
    paymentOptions = ['发票报销', '对公转账', '劳务费申报'];
    isViewMode = () => window.location.hash.startsWith('#/app/consultant/view');

    parseExpertiseList = (expertise?: string | null): IConsultantExpertiseTag[] => {
        if (!expertise) {
            return [];
        }
        const now = new Date().toISOString();
        try {
            const list = JSON.parse(expertise);
            if (Array.isArray(list)) {
                return list
                    .map((item) => {
                        if (typeof item === 'string') {
                            const label = item.trim();
                            if (!label) {
                                return null;
                            }
                            return {
                                id: this.generateTagId(),
                                label,
                                source: 'MANUAL',
                                reviewStatus: 'APPROVED',
                                createTime: now,
                                lastEditTime: now,
                            } as IConsultantExpertiseTag;
                        }
                        if (item && typeof item === 'object') {
                            const label = (item.label || '').toString().trim();
                            if (!label) {
                                return null;
                            }
                            return {
                                id: item.id || this.generateTagId(),
                                label,
                                category: item.category,
                                source: item.source || 'MANUAL',
                                reviewStatus: item.reviewStatus || ((item.source || '').toUpperCase() === 'AI' ? 'PENDING' : 'APPROVED'),
                                reviewRecommendation: item.reviewRecommendation,
                                aiModel: item.aiModel,
                                aiReason: item.aiReason,
                                aiConfidence: item.aiConfidence,
                                aiConfidenceScore: item.aiConfidenceScore,
                                aiDataSources: item.aiDataSources,
                                aiReasoningProcess: item.aiReasoningProcess,
                                promptTemplateId: item.promptTemplateId,
                                promptVersion: item.promptVersion,
                                createTime: item.createTime || now,
                                lastEditTime: item.lastEditTime || now,
                            } as IConsultantExpertiseTag;
                        }
                        return null;
                    })
                    .filter((item): item is IConsultantExpertiseTag => !!item);
            }
        } catch (e) {
            // fall through and use plain text split
        }
        return expertise
            .split(/[\n,，]/g)
            .map(item => item.trim())
            .filter(item => !!item)
            .map((item) => ({
                id: this.generateTagId(),
                label: item,
                source: 'MANUAL',
                reviewStatus: 'APPROVED',
                createTime: now,
                lastEditTime: now,
            } as IConsultantExpertiseTag));
    };

    stringifyExpertiseList = (expertiseList?: IConsultantExpertiseTag[]): string => {
        const now = new Date().toISOString();
        const list = (expertiseList || [])
            .map(item => ({
                id: item.id || this.generateTagId(),
                label: (item.label || '').trim(),
                category: item.category,
                source: item.source || 'MANUAL',
                reviewStatus: item.reviewStatus || ((item.source || '').toUpperCase() === 'AI' ? 'PENDING' : 'APPROVED'),
                reviewRecommendation: item.reviewRecommendation,
                aiModel: item.aiModel,
                aiReason: item.aiReason,
                aiConfidence: item.aiConfidence,
                aiConfidenceScore: item.aiConfidenceScore,
                aiDataSources: item.aiDataSources,
                aiReasoningProcess: item.aiReasoningProcess,
                promptTemplateId: item.promptTemplateId,
                promptVersion: item.promptVersion,
                createTime: item.createTime || now,
                lastEditTime: now,
            }))
            .filter(item => !!item.label);
        // 空列表也需要显式提交，避免 update 接口忽略该字段导致历史值残留
        return JSON.stringify(list);
    };

    generateTagId = () => `tag_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;

    sanitizeAmountInput = (input: any): string => {
        const value = (input || '').toString();
        const noInvalidChars = value.replace(/[^\d.]/g, '');
        const normalized = noInvalidChars
            .replace(/^\./, '')
            .replace(/\.{2,}/g, '.')
            .replace('.', '$#$')
            .replace(/\./g, '')
            .replace('$#$', '.');
        return normalized;
    };

    parseFeeItemList = (feeItems?: string | null): Array<{ content: string; amount: string }> => {
        if (!feeItems) {
            return [];
        }
        try {
            const list = JSON.parse(feeItems);
            if (Array.isArray(list)) {
                return list.map((item: any) => ({
                    content: item?.content || '',
                    amount: item?.amount !== undefined && item?.amount !== null ? String(item.amount) : '',
                }));
            }
        } catch (e) {
            // no-op
        }
        return [];
    };

    stringifyFeeItemList = (feeItemList?: Array<{ content?: string; amount?: any }>): string => {
        const list = (feeItemList || [])
            .map((item) => ({
                content: (item?.content || '').trim(),
                amount: this.sanitizeAmountInput(item?.amount),
            }))
            .filter((item) => !!item.content);
        return JSON.stringify(list);
    };

    buildLegacyFeeItemList = (data: IConsultantInfo): Array<{ content: string; amount: string }> => {
        const feeList = this.parseFeeItemList(data.feeItems);
        if (feeList.length > 0) {
            return feeList;
        }
        const legacy = [
            { content: '合作费用(1天)', amount: data.feeDay },
            { content: '合作费用(0.5天)', amount: data.feeHalfDay },
            { content: '线上辅导(1小时)', amount: data.feeOnlineHour },
            { content: '1v1教练(1小时)', amount: data.feeCoachHour },
        ].filter((item) => item.amount !== null && item.amount !== undefined);

        return legacy.map((item) => ({
            content: item.content,
            amount: String(item.amount),
        }));
    };

    async componentDidMount() {
        const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
        const id = parseInt(searchParams.get('id') || '0');

        if (id) {
            this.setState({ isEditMode: true, loading: true });
            const response = await ConsultantService.getConsultantById(id);
            if (response && response.data && response.data.consultantInfo) {
                const data = response.data.consultantInfo;
                const attachmentList = response.data.attachmentList || [];
                this.setState({ data, attachmentList, loading: false });
                this.formRef.current?.setFieldsValue({
                    name: data.name,
                    idCardNo: data.idCardNo,
                    residenceCity: data.residenceCity,
                    languageTags: data.languageTags,
                    feeItemList: this.buildLegacyFeeItemList(data),
                    feeSpecialRemark: data.feeSpecialRemark,
                    paymentMode: data.paymentMode,
                    expertiseList: this.parseExpertiseList(data.expertise),
                    states: data.states,
                });
            } else {
                this.setState({ loading: false });
                message.error(response ? response.message : '获取顾问信息失败');
            }
        }
    }

    handleBackButtonClick = () => {
        window.history.back();
    };

    onFinish = async (params: any) => {
        if (this.isViewMode()) {
            return;
        }
        this.setState({ loading: true });
        const payload = {
            ...params,
            feeItems: this.stringifyFeeItemList(params.feeItemList),
            expertise: this.stringifyExpertiseList(params.expertiseList),
            feeDay: undefined,
            feeHalfDay: undefined,
            feeOnlineHour: undefined,
            feeCoachHour: undefined,
        };
        delete payload.feeItemList;
        delete payload.expertiseList;

        if (!this.state.isEditMode) {
            const response = await ConsultantService.addConsultant(payload as ConsultantPostParam);
            if (response && response.success) {
                message.success(ADD_CONSULTANT_SUCCESS_MESSAGE);
                this.formRef.current?.resetFields();
            } else {
                message.error(response ? response.message : ADD_CONSULTANT_ERROR_MESSAGE);
            }
            this.setState({ loading: false });
            return;
        }

        const response = await ConsultantService.updateConsultant({
            ...(payload as ConsultantPutParam),
            id: this.state.data!.id,
        });
        if (response && response.success) {
            message.success(EDIT_CONSULTANT_SUCCESS_MESSAGE);
            setTimeout(() => window.location.reload(), 500);
        } else {
            message.error(response ? response.message : EDIT_CONSULTANT_ERROR_MESSAGE);
            setTimeout(() => window.location.reload(), 500);
        }
        this.setState({ loading: false });
    };

    handleUpload = async (file: File) => {
        if (!this.state.data) {
            message.error('请先保存顾问信息后再上传资料');
            return;
        }
        const response = await ConsultantService.uploadAttachment(this.state.data.id, file);
        if (response && response.success) {
            message.success(UPLOAD_CONSULTANT_ATTACHMENT_SUCCESS_MESSAGE);
            const listResponse = await ConsultantService.getAttachmentByConsultantId(this.state.data.id);
            this.setState({ attachmentList: listResponse?.data?.attachmentList || [] });
        } else {
            message.error(response ? response.message : UPLOAD_CONSULTANT_ATTACHMENT_ERROR_MESSAGE);
        }
    };

    deleteAttachment = async (attachmentId: number) => {
        const response = await ConsultantService.deleteAttachment(attachmentId);
        if (response && response.success) {
            message.success('删除附件成功');
            this.setState({ attachmentList: this.state.attachmentList.filter(item => item.id !== attachmentId) });
        } else {
            message.error(response ? response.message : '删除附件失败');
        }
    };

    getExpertiseTagsFromForm = (): IConsultantExpertiseTag[] => {
        const tags = this.formRef.current?.getFieldValue('expertiseList');
        return Array.isArray(tags) ? tags : [];
    };

    setExpertiseTagsToForm = (tags: IConsultantExpertiseTag[]) => {
        this.formRef.current?.setFieldsValue({ expertiseList: tags });
    };

    refreshExpertiseListFromServer = async () => {
        if (!this.state.data?.id) {
            return;
        }
        const detailResponse = await ConsultantService.getConsultantById(this.state.data.id);
        if (detailResponse && detailResponse.success && detailResponse.data?.consultantInfo) {
            const latestInfo = detailResponse.data.consultantInfo as IConsultantInfo;
            this.setExpertiseTagsToForm(this.parseExpertiseList(latestInfo.expertise));
            this.setState((prevState) => ({
                data: prevState.data ? { ...prevState.data, expertise: latestInfo.expertise } : prevState.data,
            }));
        }
    };

    isAiPendingTag = (tag: any): boolean => {
        if (!tag) {
            return false;
        }
        return (tag.source || '').toUpperCase() === 'AI' && (tag.reviewStatus || '').toUpperCase() === 'PENDING';
    };

    isAiTag = (tag: any): boolean => {
        if (!tag) {
            return false;
        }
        return (tag.source || '').toUpperCase() === 'AI';
    };

    getAiConfidenceScoreText = (tag: any): string => {
        const score = Number(tag?.aiConfidenceScore);
        if (Number.isFinite(score) && score >= 1 && score <= 10) {
            return `${Math.round(score)}`;
        }
        return '-';
    };

    getCategoryTagColor = (tag: any): string => {
        const category = (tag?.category || '').toString();
        if (category.includes('关键能力')) {
            return 'gold';
        }
        if (category.includes('擅长领域')) {
            return 'geekblue';
        }
        return 'blue';
    };

    getReviewRecommendationTagColor = (tag: any): string => {
        const recommendation = (tag?.reviewRecommendation || '').toString();
        if (!recommendation) {
            return 'default';
        }
        if (recommendation.includes('人工复核')) {
            return 'red';
        }
        if (recommendation.includes('不建议') || recommendation.includes('不保留')) {
            return 'red';
        }
        if (recommendation.includes('建议保留') || recommendation.includes('建议')) {
            return 'green';
        }
        return 'default';
    };

    getAttachmentCreateTime = (attachment: IConsultantAttachment): number => {
        const rawTime = (attachment as any)?.createTime;
        if (typeof rawTime === 'number' && Number.isFinite(rawTime)) {
            return rawTime;
        }
        if (typeof rawTime === 'string' && rawTime.trim()) {
            const normalized = rawTime.includes('T') ? rawTime : rawTime.replace(' ', 'T');
            const timestamp = Date.parse(normalized);
            if (!Number.isNaN(timestamp)) {
                return timestamp;
            }
        }
        return 0;
    };

    getOrderedAttachmentList = (): IConsultantAttachment[] => {
        return [...this.state.attachmentList].sort((a, b) => {
            const timeDiff = this.getAttachmentCreateTime(b) - this.getAttachmentCreateTime(a);
            if (timeDiff !== 0) {
                return timeDiff;
            }
            return (b.id || 0) - (a.id || 0);
        });
    };

    handleIdentifyAiTags = async () => {
        if (!this.state.data?.id) {
            message.error('请先保存顾问信息后再识别');
            return;
        }
        if (this.state.attachmentList.length === 0) {
            message.warning('使用该功能需先上传附件');
            return;
        }
        this.setState({ aiTagLoading: true });
        const response = await ConsultantService.identifyAiTags(this.state.data.id);
        if (response && response.success) {
            const tags = this.parseExpertiseList(JSON.stringify(response?.data?.expertiseTagList || []));
            this.setExpertiseTagsToForm(tags);
            const addedCount = response?.data?.addedCount || 0;
            const refreshedCount = response?.data?.refreshedCount || 0;
            if (addedCount > 0 || refreshedCount > 0) {
                const parts: string[] = [];
                if (addedCount > 0) {
                    parts.push(`新增 ${addedCount} 个`);
                }
                if (refreshedCount > 0) {
                    parts.push(`更新 ${refreshedCount} 个`);
                }
                message.success(`AI识别完成，${parts.join('，')}标签`);
            } else {
                message.info('AI识别完成，未新增标签（可能与现有标签重复）');
            }
        } else {
            message.error(response ? response.message : 'AI识别失败');
        }
        this.setState({ aiTagLoading: false });
    };

    handleApproveAiTag = async (tagId: string) => {
        if (!this.state.data?.id) {
            return;
        }
        this.setState({ aiTagLoading: true });
        const response = await ConsultantService.approveAiTag(this.state.data.id, tagId);
        if (response && response.success) {
            const tags = this.parseExpertiseList(JSON.stringify(response?.data?.expertiseTagList || []));
            this.setExpertiseTagsToForm(tags);
            message.success('已复核通过，已转为人工标签');
        } else {
            message.error(response ? response.message : '复核通过失败');
        }
        this.setState({ aiTagLoading: false });
    };

    handleRejectAiTag = (tagId: string) => {
        if (!this.state.data?.id) {
            return;
        }
        let reason = '';
        let suggestion = '';
        Modal.confirm({
            title: '确认复核不通过',
            okText: '确认',
            cancelText: '取消',
            content: (
                <div className="consultant-ai-reject-form">
                    <div className="consultant-ai-reject-label">不通过理由（可选）</div>
                    <Input.TextArea rows={3} placeholder="请输入不通过理由" onChange={(e) => { reason = e.target.value; }} />
                    <div className="consultant-ai-reject-label">改善建议（可选）</div>
                    <Input.TextArea rows={3} placeholder="请输入改善建议" onChange={(e) => { suggestion = e.target.value; }} />
                </div>
            ),
            onOk: async () => {
                this.setState({ aiTagLoading: true });
                const response = await ConsultantService.rejectAiTag(this.state.data!.id, tagId, reason, suggestion);
                if (response && response.success) {
                    await this.refreshExpertiseListFromServer();
                    message.success('已移除AI标签');
                } else {
                    message.error(response ? response.message : '复核不通过操作失败');
                }
                this.setState({ aiTagLoading: false });
            },
        });
    };

    render() {
        const { isEditMode, data, aiTagLoading } = this.state;
        const orderedAttachmentList = this.getOrderedAttachmentList();
        const isViewMode = this.isViewMode();
        const hasAttachment = orderedAttachmentList.length > 0;

        const initialValues = {
            states: isEditMode && data ? data.states : CooperationStates.normal,
            feeItemList: isEditMode && data ? this.buildLegacyFeeItemList(data) : [],
            expertiseList: isEditMode && data ? this.parseExpertiseList(data.expertise) : [],
        };

        return (
            <div className={`page-consultant_container ${isViewMode ? 'consultant-view-mode' : ''}`}>
                <BreadcrumbCustom breads={['顾问管理', isViewMode ? '查看顾问' : (isEditMode ? '编辑顾问' : '新建顾问')]} />
                <Card
                    title={
                        <div className="title-manager_page">
                            <Button type="link" className="back-button" onClick={this.handleBackButtonClick}>{'< 返回'}</Button>
                            {isViewMode ? '查看顾问' : (isEditMode ? '编辑顾问' : '新建顾问')}
                        </div>
                    }
                    bordered={false}
                >
                    <div className="consultant-form_container">
                        <Form ref={this.formRef} onFinish={this.onFinish} initialValues={initialValues}>
                            <Form.Item label="姓名" name="name" className="formItem-consultant" rules={[{ required: true, message: '请输入姓名' }]}>
                                <Input placeholder="请输入姓名" autoComplete="off" />
                            </Form.Item>

                            <Form.Item label="身份证" name="idCardNo" className="formItem-consultant">
                                <Input placeholder="请输入身份证号码" autoComplete="off" />
                            </Form.Item>

                            <Form.Item label="居住城市" name="residenceCity" className="formItem-consultant">
                                <Input placeholder="请输入居住城市" autoComplete="off" />
                            </Form.Item>

                            <Form.Item label="语言" name="languageTags" className="formItem-consultant">
                                <Input placeholder="中文,英文,其他" autoComplete="off" />
                            </Form.Item>

                            <Form.Item label="合作费用" className="formItem-consultant_text formItem-consultant_fee-list">
                                <Form.List name="feeItemList">
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map((field) => (
                                                <div key={field.key} className="consultant-row-inline">
                                                    <Form.Item
                                                        {...field}
                                                        name={[field.name, 'content']}
                                                        rules={[{ required: true, message: '请输入费用内容' }]}
                                                        className="consultant-fee-content"
                                                    >
                                                        <Input placeholder="费用内容（例如：1天驻场）" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        {...field}
                                                        name={[field.name, 'amount']}
                                                        className="consultant-fee-amount"
                                                        getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}
                                                    >
                                                        <Input placeholder="费用金额" autoComplete="off" />
                                                    </Form.Item>
                                                    {!isViewMode && <MinusCircleOutlined className="consultant-remove-icon" onClick={() => remove(field.name)} />}
                                                </div>
                                            ))}
                                            {!isViewMode && (
                                                <Form.Item className="consultant-add-row">
                                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                                                        新增合作费用项
                                                    </Button>
                                                </Form.Item>
                                            )}
                                        </>
                                    )}
                                </Form.List>
                            </Form.Item>

                            <Form.Item label="费用特殊备注" name="feeSpecialRemark" className="formItem-consultant_text">
                                <Input.TextArea placeholder="请输入合作费用的特殊说明（如阶梯价格、打包价、临时折扣等）" rows={2} />
                            </Form.Item>

                            <Form.Item label="支付方式" name="paymentMode" className="formItem-consultant">
                                <Select allowClear={!isViewMode} placeholder="请选择支付方式">
                                    {this.paymentOptions.map((item) => (
                                        <Select.Option key={item} value={item}>{item}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="擅长领域/关键能力" className="formItem-consultant_text formItem-consultant_expertise">
                                <Form.List name="expertiseList">
                                    {(fields, { add, remove }) => (
                                        <>
                                            {fields.map((field) => {
                                                const currentTag = this.formRef.current?.getFieldValue(['expertiseList', field.name]);
                                                const isAiTag = this.isAiTag(currentTag);
                                                const isAiPendingTag = this.isAiPendingTag(currentTag);
                                                const confidenceScoreText = this.getAiConfidenceScoreText(currentTag);
                                                return (
                                                <div key={field.key} className={`consultant-row-inline ${isAiTag ? 'consultant-row-inline-ai' : ''}`}>
                                                    <Form.Item
                                                        {...field}
                                                        name={[field.name, 'label']}
                                                        rules={[{ required: true, message: '请输入擅长领域或关键能力' }]}
                                                        className="consultant-expertise-input"
                                                    >
                                                        <Input
                                                            placeholder="例如：组织发展、教练辅导、销售赋能"
                                                            disabled={isViewMode || isAiPendingTag}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'id']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'category']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'source']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'reviewStatus']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'reviewRecommendation']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'aiModel']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'aiReason']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'aiConfidence']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'aiConfidenceScore']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'aiDataSources']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'aiReasoningProcess']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'promptTemplateId']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'promptVersion']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'createTime']} hidden><Input /></Form.Item>
                                                    <Form.Item {...field} name={[field.name, 'lastEditTime']} hidden><Input /></Form.Item>

                                                    {!isViewMode && !isAiPendingTag && (
                                                        <MinusCircleOutlined className="consultant-remove-icon" onClick={() => remove(field.name)} />
                                                    )}

                                                    {isAiTag && (
                                                        <div className="consultant-ai-tag-panel">
                                                            <Tooltip
                                                                title={
                                                                    <>
                                                                        <div>标签类别：{currentTag?.category || '-'}</div>
                                                                        <div>置信度：{confidenceScoreText}/10</div>
                                                                        <div>复核建议：{currentTag?.reviewRecommendation || '-'}</div>
                                                                        <div>数据源：{currentTag?.aiDataSources || '-'}</div>
                                                                        <div>推理摘要：{currentTag?.aiReasoningProcess || currentTag?.aiReason || '-'}</div>
                                                                    </>
                                                                }
                                                            >
                                                                <div className="consultant-ai-tag-meta">
                                                                    <div className="consultant-ai-tag-line">
                                                                        <Tag color="blue" className="consultant-ai-tag-equal-width">AI智能标签</Tag>
                                                                        <Tag color={this.getCategoryTagColor(currentTag)} className="consultant-ai-tag-wide">
                                                                            {currentTag?.category || '擅长领域/关键能力'}
                                                                        </Tag>
                                                                    </div>
                                                                    <div className="consultant-ai-tag-line">
                                                                        <Tag color={isAiPendingTag ? 'processing' : 'default'} className="consultant-ai-tag-equal-width">
                                                                            置信度 {confidenceScoreText}/10
                                                                        </Tag>
                                                                        <Tag color={this.getReviewRecommendationTagColor(currentTag)} className="consultant-ai-tag-wide">
                                                                            {currentTag?.reviewRecommendation || '-'}
                                                                        </Tag>
                                                                    </div>
                                                                </div>
                                                            </Tooltip>
                                                            {isAiPendingTag && (
                                                                <div className="consultant-ai-review-actions">
                                                                    <Button
                                                                        size="small"
                                                                        type="link"
                                                                        onClick={() => this.handleApproveAiTag(currentTag?.id)}
                                                                        disabled={aiTagLoading}
                                                                    >
                                                                        复核通过
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        danger
                                                                        type="link"
                                                                        onClick={() => this.handleRejectAiTag(currentTag?.id)}
                                                                        disabled={aiTagLoading}
                                                                    >
                                                                        复核不通过
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                            {!isViewMode && (
                                                <div className="consultant-expertise-toolbar">
                                                    <Form.Item className="consultant-add-row">
                                                        <Button
                                                            type="dashed"
                                                            onClick={() => add({
                                                                id: this.generateTagId(),
                                                                label: '',
                                                                source: 'MANUAL',
                                                                reviewStatus: 'APPROVED',
                                                            })}
                                                            icon={<PlusOutlined />}
                                                        >
                                                            新增擅长领域/关键能力
                                                        </Button>
                                                    </Form.Item>
                                                    <Tooltip title={!hasAttachment ? '使用该功能需先上传附件' : ''}>
                                                        <span>
                                                            <Button
                                                                className="consultant-ai-identify-btn"
                                                                onClick={this.handleIdentifyAiTags}
                                                                disabled={!hasAttachment || aiTagLoading}
                                                            >
                                                                {aiTagLoading ? (
                                                                    <>
                                                                        <LoadingOutlined spin className="consultant-ai-identify-loading-icon" />
                                                                        AI识别中...
                                                                    </>
                                                                ) : (
                                                                    'AI识别关键能力'
                                                                )}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Form.List>
                            </Form.Item>

                            <Form.Item label="状态" name="states" className="formItem-consultant_state">
                                <CooperationStatesSelect showAllOption={false} initialValues={initialValues.states} />
                            </Form.Item>

                            {isEditMode && data && (
                                <Form.Item label="资料库" className="formItem-consultant_attachment">
                                    {!isViewMode && (
                                        <Upload
                                            multiple
                                            beforeUpload={(file) => {
                                                this.handleUpload(file as unknown as File);
                                                return false;
                                            }}
                                            showUploadList={false}
                                        >
                                            <Button icon={<UploadOutlined />}>上传附件</Button>
                                        </Upload>
                                    )}

                                    <List
                                        className="consultant-attachment-list"
                                        size="small"
                                        header={(
                                            <div className="consultant-attachment-table-head">
                                                <span className="consultant-attachment-col-id">ID</span>
                                                <span className="consultant-attachment-col-name">文件名</span>
                                                <span className="consultant-attachment-col-actions" />
                                            </div>
                                        )}
                                        dataSource={orderedAttachmentList}
                                        renderItem={(item, index) => (
                                            <List.Item>
                                                <div className="consultant-attachment-table-row">
                                                    <span className="consultant-attachment-col-id">{index + 1}</span>
                                                    <span className="consultant-attachment-col-name">{item.fileName}</span>
                                                    <span className="consultant-attachment-col-actions">
                                                        <a href={buildFileUrl(item.fileUrl)} target="_blank" rel="noopener noreferrer">查看</a>
                                                        {!isViewMode && (
                                                            <button type="button" onClick={() => this.deleteAttachment(item.id)}>删除</button>
                                                        )}
                                                    </span>
                                                </div>
                                            </List.Item>
                                        )}
                                    />
                                </Form.Item>
                            )}

                            {!isViewMode && (
                                <Form.Item>
                                    <Button type="primary" className="button-consultant_submit" htmlType="submit" loading={this.state.loading}>
                                        提交
                                    </Button>
                                </Form.Item>
                            )}
                        </Form>
                    </div>
                </Card>
            </div>
        );
    }
}

export default ConsultantManagerPage;
